<div class="model-manager-container">
  <!-- Header Section -->
  <div class="header-section">
    <div class="header-content">
      <h2 class="page-title">
        <i class="fas fa-car me-2"></i>
        Vehicle Model Manager
      </h2>
      <p class="page-subtitle">Manage vehicle makes and models across different product types</p>
    </div>
    <div class="header-actions">
      <button type="button" class="btn btn-outline-success" (click)="exportData()">
        <i class="fas fa-download me-1"></i>
        Export Data
      </button>
    </div>
  </div>

  <!-- Summary Cards -->
  <div class="summary-cards row g-3 mb-4">
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="summary-card">
        <div class="card-icon">
          <i class="fas fa-industry"></i>
        </div>
        <div class="card-content">
          <h3>{{ totalMakes }}</h3>
          <p>Total Makes</p>
        </div>
      </div>
    </div>
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="summary-card">
        <div class="card-icon">
          <i class="fas fa-car"></i>
        </div>
        <div class="card-content">
          <h3>{{ totalModels }}</h3>
          <p>Total Models</p>
        </div>
      </div>
    </div>
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="summary-card">
        <div class="card-icon">
          <i class="fas fa-motorcycle"></i>
        </div>
        <div class="card-content">
          <h3>{{ twoWheelerCount }}</h3>
          <p>Two Wheeler Makes</p>
        </div>
      </div>
    </div>
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="summary-card">
        <div class="card-icon">
          <i class="fas fa-truck"></i>
        </div>
        <div class="card-content">
          <h3>{{ fourWheelerCount }}</h3>
          <p>Four Wheeler Makes</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="row g-4">
    <!-- Add New Make Form -->
    <div class="col-12 col-lg-4">
      <div class="card form-card">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="fas fa-plus-circle me-2"></i>
            Add New Make
          </h5>
        </div>
        <div class="card-body">
          <form [formGroup]="modelForm" (ngSubmit)="onSubmit()">
            <!-- Make Name -->
            <div class="mb-3">
              <label for="make" class="form-label">Make Name *</label>
              <input
                type="text"
                id="make"
                class="form-control"
                [class.is-invalid]="isFieldInvalid('make')"
                formControlName="make"
                placeholder="Enter make name">
              <div class="invalid-feedback" *ngIf="isFieldInvalid('make')">
                {{ getFieldError('make') }}
              </div>
            </div>

            <!-- Product Type -->
            <div class="mb-3">
              <label for="productType" class="form-label">Product Type *</label>
              <select
                id="productType"
                class="form-select"
                [class.is-invalid]="isFieldInvalid('productType')"
                formControlName="productType">
                <option value="">Select Product Type</option>
                <option *ngFor="let type of productTypes" [value]="type">{{ type }}</option>
              </select>
              <div class="invalid-feedback" *ngIf="isFieldInvalid('productType')">
                {{ getFieldError('productType') }}
              </div>
            </div>

            <!-- Models -->
            <div class="mb-3">
              <label class="form-label">Models *</label>
              <div formArrayName="models">
                <div *ngFor="let model of modelsArray.controls; let i = index" 
                     class="model-input-group mb-2" 
                     [formGroupName]="i">
                  <div class="input-group">
                    <input
                      type="text"
                      class="form-control"
                      formControlName="name"
                      placeholder="Enter model name">
                    <button
                      type="button"
                      class="btn btn-outline-danger"
                      [disabled]="modelsArray.length === 1"
                      (click)="removeModelField(i)">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                (click)="addModelField()">
                <i class="fas fa-plus me-1"></i>
                Add Model
              </button>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              class="btn btn-primary w-100"
              [disabled]="submitting || !modelForm.valid">
              <span *ngIf="submitting" class="spinner-border spinner-border-sm me-2"></span>
              <i *ngIf="!submitting" class="fas fa-save me-2"></i>
              {{ submitting ? 'Saving...' : 'Save Make' }}
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- Makes List -->
    <div class="col-12 col-lg-8">
      <div class="card data-card">
        <div class="card-header">
          <h5 class="card-title mb-0">
            <i class="fas fa-list me-2"></i>
            Vehicle Makes & Models
          </h5>
        </div>
        <div class="card-body">
          <!-- Filters -->
          <div class="filters-section row g-3 mb-4">
            <div class="col-12 col-md-6">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  class="form-control"
                  placeholder="Search makes or models..."
                  [(ngModel)]="searchTerm"
                  (input)="applyFilters()">
              </div>
            </div>
            <div class="col-12 col-md-4">
              <select
                class="form-select"
                [(ngModel)]="selectedProductType"
                (change)="applyFilters()">
                <option value="">All Product Types</option>
                <option *ngFor="let type of productTypes" [value]="type">{{ type }}</option>
              </select>
            </div>
            <div class="col-12 col-md-2">
              <button
                type="button"
                class="btn btn-outline-secondary w-100"
                (click)="clearFilters()">
                <i class="fas fa-times me-1"></i>
                Clear
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div *ngIf="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2 text-muted">Loading model data...</p>
          </div>

          <!-- No Data State -->
          <div *ngIf="!loading && filteredData.length === 0" class="text-center py-4">
            <i class="fas fa-exclamation-circle fa-3x text-muted mb-3"></i>
            <h5 class="text-muted">No data found</h5>
            <p class="text-muted">
              {{ searchTerm || selectedProductType ? 'No results match your filters.' : 'No makes have been added yet.' }}
            </p>
          </div>

          <!-- Makes List -->
          <div *ngIf="!loading && filteredData.length > 0" class="makes-list">
            <div *ngFor="let make of filteredData; trackBy: trackByMake" class="make-item">
              <div class="make-header">
                <div class="make-info">
                  <h6 class="make-name">{{ make.make }}</h6>
                  <span class="product-type-badge" [ngClass]="'badge-' + make.productType.replace(' ', '-').toLowerCase()">
                    {{ make.productType }}
                  </span>
                  <span class="model-count">{{ make.model.length }} models</span>
                </div>
                <div class="make-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary me-2"
                    (click)="editMake(make, editModal)">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    (click)="deleteMake(make)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>

              <div class="models-section">
                <div class="models-grid">
                  <span *ngFor="let model of make.model" class="model-tag">
                    {{ model }}
                    <button
                      type="button"
                      class="model-delete-btn"
                      (click)="deleteModel(make, model)">
                      <i class="fas fa-times"></i>
                    </button>
                  </span>
                </div>
                <div class="add-model-section">
                  <div class="input-group input-group-sm">
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Add new model..."
                      [(ngModel)]="newModelName"
                      (keyup.enter)="addModel(make)">
                    <button
                      type="button"
                      class="btn btn-outline-success"
                      (click)="addModel(make)"
                      [disabled]="!newModelName.trim()">
                      <i class="fas fa-plus"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Edit Make Modal -->
<ng-template #editModal let-modal>
  <div class="modal-header">
    <h4 class="modal-title">Edit Make</h4>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    <form [formGroup]="editForm" (ngSubmit)="updateMake()">
      <div class="mb-3">
        <label for="editMake" class="form-label">Make Name *</label>
        <input
          type="text"
          id="editMake"
          class="form-control"
          [class.is-invalid]="isFieldInvalid('make', editForm)"
          formControlName="make">
        <div class="invalid-feedback" *ngIf="isFieldInvalid('make', editForm)">
          {{ getFieldError('make', editForm) }}
        </div>
      </div>
      <div class="mb-3">
        <label for="editProductType" class="form-label">Product Type *</label>
        <select
          id="editProductType"
          class="form-select"
          [class.is-invalid]="isFieldInvalid('productType', editForm)"
          formControlName="productType">
          <option value="">Select Product Type</option>
          <option *ngFor="let type of productTypes" [value]="type">{{ type }}</option>
        </select>
        <div class="invalid-feedback" *ngIf="isFieldInvalid('productType', editForm)">
          {{ getFieldError('productType', editForm) }}
        </div>
      </div>
    </form>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-secondary" (click)="modal.dismiss()">
      Cancel
    </button>
    <button type="button" class="btn btn-primary" (click)="updateMake()" [disabled]="!editForm.valid">
      Update Make
    </button>
  </div>
</ng-template>
